/**
 * HintManager - Handles displaying hint tooltips for puzzle words
 * Supports long-press (mobile) and hover delay (desktop)
 */
export class HintManager {
    constructor() {
        this.tooltip = null;
        this.tooltipText = null;
        this.hideTimeout = null;
        this.showTimeout = null;
        this.LONG_PRESS_DELAY = 500; // ms before showing hint
        this.AUTO_HIDE_DELAY = 3000; // ms before auto-hiding
        this.isDragActive = false;
        this.lastTouchTime = 0; // Timestamp of last touch to suppress synthetic mouse events
        this.initialized = false;

        // Store bound handlers for cleanup
        this.documentClickHandler = null;
        this.documentTouchStartHandler = null;
        this.documentTouchEndHandler = null;
    }

    init() {
        // Prevent multiple initializations - destroy first if re-initializing
        if (this.initialized) {
            this.destroy();
        }

        this.tooltip = document.getElementById('hint-tooltip');
        this.tooltipText = document.getElementById('hint-tooltip-text');

        // Create bound handlers so we can remove them later
        this.documentClickHandler = () => this.hide();
        this.documentTouchStartHandler = () => {
            this.hide();
            document.body.classList.add('touch-active');
        };
        this.documentTouchEndHandler = () => {
            // Remove after a delay to cover animations and re-renders
            setTimeout(() => {
                document.body.classList.remove('touch-active');
            }, 1500);
        };

        // Hide on any tap/click outside
        document.addEventListener('click', this.documentClickHandler);
        document.addEventListener('touchstart', this.documentTouchStartHandler, { passive: true });
        document.addEventListener('touchend', this.documentTouchEndHandler, { passive: true });

        this.initialized = true;
    }

    /**
     * Attach hint handlers to an element
     * @param {HTMLElement} element - The button/item to attach to
     * @param {string} hintText - The hint to display
     */
    attachHintHandlers(element, hintText) {
        if (!hintText) return;

        // Mobile: long-press
        let touchTimeout = null;

        element.addEventListener('touchstart', (e) => {
            // Mark that we're in a touch interaction to suppress synthetic mouse events
            // Use a timestamp instead of a boolean so we can check elapsed time
            this.lastTouchTime = Date.now();

            touchTimeout = setTimeout(() => {
                this.show(hintText, element);
            }, this.LONG_PRESS_DELAY);
        }, { passive: true });

        element.addEventListener('touchend', () => {
            clearTimeout(touchTimeout);
            // Update touch time on end as well
            this.lastTouchTime = Date.now();
        });

        element.addEventListener('touchmove', () => {
            clearTimeout(touchTimeout);
            this.hide(); // Hide tooltip if user starts moving
        });

        element.addEventListener('touchcancel', () => {
            clearTimeout(touchTimeout);
        });

        // Desktop: hover delay (only activates when not from recent touch)
        let hoverTimeout = null;

        element.addEventListener('mouseenter', () => {
            // Skip if this is a synthetic mouse event from recent touch or during animations
            // Use 1500ms window to cover animation + re-render timing
            const timeSinceTouch = Date.now() - (this.lastTouchTime || 0);
            if (timeSinceTouch < 1500 || this.isDragActive) return;

            hoverTimeout = setTimeout(() => {
                this.show(hintText, element);
            }, this.LONG_PRESS_DELAY);
        });

        element.addEventListener('mouseleave', () => {
            clearTimeout(hoverTimeout);
            this.hide();
        });
    }

    /**
     * Show the hint tooltip near the element
     */
    show(text, element) {
        if (!this.tooltip || !this.tooltipText || this.isDragActive) return;

        // Check if element is still in the DOM (may have been re-rendered)
        if (!document.body.contains(element)) return;

        this.tooltipText.textContent = text;

        // Position tooltip near the element
        const rect = element.getBoundingClientRect();
        const tooltipRect = this.tooltip.getBoundingClientRect();

        // Default: above the element, centered
        let left = rect.left + (rect.width / 2) - 140; // 140 = half of max-width
        let top = rect.top - 60; // Above the element

        // Keep within viewport
        left = Math.max(10, Math.min(left, window.innerWidth - 290));
        top = Math.max(10, top);

        // If not enough room above, show below
        if (top < 10) {
            top = rect.bottom + 10;
        }

        this.tooltip.style.left = left + 'px';
        this.tooltip.style.top = top + 'px';
        this.tooltip.classList.add('visible');

        // Auto-hide after delay
        clearTimeout(this.hideTimeout);
        this.hideTimeout = setTimeout(() => this.hide(), this.AUTO_HIDE_DELAY);
    }

    hide() {
        if (this.tooltip) {
            this.tooltip.classList.remove('visible');
        }
        clearTimeout(this.hideTimeout);
    }

    /**
     * Disable hints (called when drag starts)
     */
    disableHints() {
        this.isDragActive = true;
        this.hide();
    }

    /**
     * Re-enable hints (called when drag ends)
     */
    enableHints() {
        this.isDragActive = false;
    }

    /**
     * Clean up document-level event listeners to prevent memory leaks
     */
    destroy() {
        this.hide();

        if (this.documentClickHandler) {
            document.removeEventListener('click', this.documentClickHandler);
            this.documentClickHandler = null;
        }
        if (this.documentTouchStartHandler) {
            document.removeEventListener('touchstart', this.documentTouchStartHandler);
            this.documentTouchStartHandler = null;
        }
        if (this.documentTouchEndHandler) {
            document.removeEventListener('touchend', this.documentTouchEndHandler);
            this.documentTouchEndHandler = null;
        }

        this.tooltip = null;
        this.tooltipText = null;
        this.initialized = false;
    }
}

// Singleton instance
export const hintManager = new HintManager();
